import { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  API_PATHS,
  INTERACTION_COLORS,
  BORDERS,
  ANIMATION_CONFIG,
  PLANNING_TOOLTIP_MESSAGES,
} from "../../../../config";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { CaptureContext } from "../../../../context/domain/planning/CaptureContext";

import LinkifiedTextArea from "../../../../components/common/LinkifiedTextArea";
import WorkScopeModal from "../WorkScopeModal";
import ScopeWorkersSection from "./ScopeWorkersSection";
import { AddressMapsLink, ConfirmModal } from "../../../../components/ui";
import { ScopeDetailsSection } from "./ScopeDetailsSection";

import {
  formatDate,
  getDateInLocalTZFromDateStr,
  getDateString,
  timeToString,
} from "../../../../utils/datetime";
import { debounce } from "../../../../utils/common";
import { getAddresses, updateWorkScopeAspect } from "../../../../services/api/dispatch";
import {
  patchPlanningScopeBlock,
  updateScope as updatePlanningScopeAspect,
} from "../../../../services/api/planning";
import { apiRequest } from "../../../../services/api/axios";

import { DISPATCH_DAY_LOCKED_UI } from "../constants";
import SkippedDayOverlay from "../../shared/SkippedDayOverlay";
import { GoPencil } from "react-icons/go";
import { IoEllipsisHorizontal } from "react-icons/io5";

/**
 * Return a nested entity id, preserving primitive ids from API write payloads.
 *
 * @param {Object|number|string|null|undefined} value - Nested object or primitive id.
 * @returns {number|string|null}
 */
function getEntityId(value) {
  if (value == null || value === "") {
    return null;
  }
  return typeof value === "object" ? value.id ?? null : value;
}

/**
 * Copy JSON-backed extra data sections without sharing object references.
 *
 * @param {Array<Object>} sections - Dispatch extra data sections.
 * @returns {Array<Object>}
 */
function cloneExtraDataSections(sections) {
  return (sections ?? []).map((section) => ({ ...section }));
}

/**
 * Build write payload rows for concrete assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized concrete assignment rows.
 * @returns {Array<Object>}
 */
function copyConcreteAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      mix: getEntityId(assignment.mix),
      time: assignment.time ?? null,
      yards: assignment.yards ?? null,
    }))
    .filter((assignment) => assignment.mix != null);
}

/**
 * Build write payload rows for equipment assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized equipment assignment rows.
 * @returns {Array<Object>}
 */
function copyEquipmentAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      asset_id: getEntityId(assignment.asset),
      rent: Boolean(assignment.rent),
    }))
    .filter((assignment) => assignment.asset_id != null);
}

/**
 * Build write payload rows for subcontractor assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized subcontractor assignment rows.
 * @returns {Array<Object>}
 */
function copySubcontractorAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      contractor: getEntityId(assignment.contractor),
      hours: assignment.hours ?? null,
    }))
    .filter((assignment) => assignment.contractor != null);
}

/**
 * Build write payload rows for dumping assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized dumping assignment rows.
 * @returns {Array<Object>}
 */
function copyDumpingAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      contractor: getEntityId(assignment.contractor),
      quantity: assignment.quantity ?? null,
    }))
    .filter((assignment) => assignment.contractor != null);
}

/**
 * Component for the work scope box in the Daily Dispatch app.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {number} props.index - Scope index within the day.
 * @param {Date} props.date - Calendar date for this scope card.
 * @param {Object} props.scope - WorkScopeAspect row rendered in details view.
 * @param {boolean} props.nullScope - Whether this row is an empty placeholder.
 * @returns {JSX.Element}
 */
export function EditScopeCard({ index, date, scope, nullScope }) {
  const {
    updateScope,
    copyScopeOrigin,
    copyScopesDate,
    scopesToDelete,
    isReadOnly,
    runPlanningOperation,
    openScopeActionMenu,
    dayDetails,
  } = useContext(DispatchContext);
  const { reloadScopes, applyScopePatch } = useContext(WeeklyContext);
  const captureContext = useContext(CaptureContext);
  // We need to get captureComponent like this as captureContext is not always available
  var captureComponent = false;
  if (captureContext) {
    captureComponent = captureContext.captureComponent;
  }

  const [notes, setNotes] = useState(scope.notes || "");
  const [extraDataSections, setExtraDataSections] = useState(
    JSON.parse(JSON.stringify(scope.extra_data_sections ?? []))
  );
  const extraDataSectionsRef = useRef(extraDataSections);
  extraDataSectionsRef.current = extraDataSections;

  const [isEditScopeModalOpen, setIsEditScopeModalOpen] = useState(false);
  const [pendingExtraDataSectionDeleteIndex, setPendingExtraDataSectionDeleteIndex] =
    useState(null);
  const copyScopesDateMatch = getDateString(copyScopesDate) === scope.date;

  /**
   * True when Dispatch DayDetails for this scope's calendar day has locked set on the server.
   * @param {void}
   * @returns {boolean}
   */
  const isDayLocked = useMemo(() => {
    if (!dayDetails?.length) {
      return false;
    }
    return Boolean(
      dayDetails.find(
        (d) =>
          d.date ===
          formatDate(getDateInLocalTZFromDateStr(scope.date), "YYYY-MM-DD")
      )?.locked
    );
  }, [dayDetails, scope.date]);

  /**
   * Open the shared Dispatch scope action menu from the edit-card ellipsis button.
   * @param {MouseEvent} event Click on the card action button.
   * @returns {void}
   */
  const handleOpenScopeActionMenu = useCallback(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      openScopeActionMenu({
        scope,
        date,
        anchorRect: {
          top: rect.top,
          right: rect.right,
        },
      });
    },
    [date, openScopeActionMenu, scope]
  );

  /**
   * Create a one-day block for this scope change, then skip the original aspect.
   *
   * @param {Object} params
   * @param {Object} params.scopeObject Next catalog work scope.
   * @param {number|string} params.quantity Next quantity.
   * @returns {Promise<void>}
   */
  const handleChangeScopeForDayOnly = useCallback(async ({ scopeObject, quantity }) => {
    if (
      !scopeObject?.id ||
      !scope.schedule_block?.id ||
      !scope.job?.id
    ) {
      return;
    }

    const scheduleBlockId = scope.schedule_block.id;
    const workerIds = (scope.worker_assignment ?? [])
      .map((worker) => worker.id)
      .filter((id) => id != null && id !== "");
    const normalizedQuantity = quantity === "" ? 0 : quantity;

    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "changeDispatchScopeForDayOnly",
        resources: ["dispatch.aspects", "schedule.blocks", "schedule.aspects"],
        entityIds: [scope.id, scheduleBlockId],
        apiCall: async () => {
          const sourceBlock = await apiRequest(
            `${API_PATHS.SCHEDULE.BLOCKS}${scheduleBlockId}/`,
            null,
            { method: "GET" }
          );
          const createdBlock = await apiRequest(
            API_PATHS.SCHEDULE.BLOCKS,
            {
              job_id: scope.job.id,
              work_scope_id: scopeObject.id,
              schedule_group_id: sourceBlock?.schedule_group?.id ?? null,
              start_date: scope.date,
              end_date: scope.date,
              working_days_only: false,
              worker_ids: workerIds,
              merge_adjacent_blocks: true,
            },
            { method: "POST" }
          );
          const createdAspect =
            (createdBlock?.aspects ?? []).find((aspect) => aspect.date === scope.date) ??
            createdBlock?.aspects?.[0];

          if (!createdAspect?.id) {
            throw new Error("Schedule block create did not return a day aspect.");
          }

          // Skip the original day before copying details. A failed detail copy
          // must not leave this date active on the old block.
          await updatePlanningScopeAspect(scope.id, { skipped: true }, "dispatch");

          const aspectPatch = {
            quantity: normalizedQuantity,
            skipped: false,
            skipped_reason: scope.skipped_reason ?? "",
            notes: notes ?? "",
            extra_data_sections: cloneExtraDataSections(extraDataSectionsRef.current),
            worker_assignment: workerIds,
            concrete: copyConcreteAssignments(scope.concrete),
            equipment: copyEquipmentAssignments(scope.equipment),
            subcontractor: copySubcontractorAssignments(scope.subcontractor),
            dumping: copyDumpingAssignments(scope.dumping),
          };

          if (scope.custom_time) {
            aspectPatch.custom_time = scope.custom_time;
          }

          if (scope.custom_address) {
            try {
              const addresses = await getAddresses();
              const matchingAddress = (addresses ?? []).find(
                (address) => address.address === scope.custom_address
              );
              if (matchingAddress?.id) {
                aspectPatch.custom_address = matchingAddress.id;
              }
            } catch (addressError) {
              console.error("Could not resolve custom address for copied scope", addressError);
            }
          }

          await updatePlanningScopeAspect(createdAspect.id, aspectPatch, "dispatch");
          return createdBlock;
        },
      });
      applyScopePatch(scope.id, { skipped: true });
    } catch (e) {
      console.error(`could not change scope for day only on scope id ${scope.id}`, e);
    } finally {
      reloadScopes();
    }
  }, [
    applyScopePatch,
    reloadScopes,
    runPlanningOperation,
    scope,
    notes,
  ]);

  /**
   * Persist catalog/quantity edits. A multi-day block scope change runs the day-only split.
   *
   * @param {Object} params
   * @param {Object} params.scopeObject Next catalog work scope.
   * @param {number|string} params.quantity Next quantity.
   * @returns {Promise<void>}
   */
  const persistWorkScopeAspectChange = useCallback(
    async ({ scopeObject, quantity }) => {
      const scheduleBlock = scope.schedule_block ?? null;
      const scheduleBlockId = scheduleBlock?.id ?? null;
      const blockAspectCount = Number(scheduleBlock?.aspect_count ?? 0);
      const scopeChanged =
        scopeObject?.id != null && String(scopeObject.id) !== String(scope.scope?.id);
      const quantityChanged = String(quantity ?? "") !== String(scope.quantity ?? "");
      const shouldPatchBlock = Boolean(scheduleBlockId && scopeChanged);
      const shouldChangeDayOnly = shouldPatchBlock && blockAspectCount > 1;

      if (shouldChangeDayOnly) {
        setIsEditScopeModalOpen(false);
        await handleChangeScopeForDayOnly({ scopeObject, quantity });
        return;
      }

      const resources = shouldPatchBlock
        ? ["dispatch.aspects", "schedule.blocks", "schedule.aspects"]
        : ["dispatch.aspects"];
      const entityIds = shouldPatchBlock
        ? [scope.id, scheduleBlockId]
        : [scope.id];

      try {
        await runPlanningOperation({
          module: "dispatch",
          operation: "updateDispatchScope",
          resources,
          entityIds,
          apiCall: async () => {
            if (shouldPatchBlock) {
              await patchPlanningScopeBlock(scheduleBlockId, {
                work_scope_id: scopeObject.id,
              });
            }
            if (!shouldPatchBlock || quantityChanged) {
              const payload = shouldPatchBlock
                ? { quantity }
                : { scope: scopeObject, quantity };
              return updateWorkScopeAspect(scope.id, payload);
            }
            return null;
          },
        });
      } catch (e) {
        console.error(
          `could not update work scope aspect on scope id ${scope.id}`,
          e
        );
      }
      setIsEditScopeModalOpen(false);
      reloadScopes();
    },
    [
      handleChangeScopeForDayOnly,
      scope.id,
      scope.quantity,
      scope.schedule_block,
      scope.scope?.id,
      reloadScopes,
      runPlanningOperation,
    ]
  );

  /**
   * Apply a scope/quantity change from the edit modal.
   *
   * @param {Object} scopeObject Next catalog work scope.
   * @param {number|string} quantity Next quantity.
   * @returns {void}
   */
  const handleEditWorkScopeAspect = useCallback(
    (scopeObject, quantity) => {
      void persistWorkScopeAspectChange({ scopeObject, quantity });
    },
    [persistWorkScopeAspectChange]
  );

  // Main scope notes field (WorkScopeAspects.notes)
  /**
   * Debounce-save the main notes field while the user types.
   *
   * @param {import('react').ChangeEvent<HTMLTextAreaElement>} e Notes input event.
   * @returns {void}
   */
  const handleEditNotes = useCallback(
    (e) => {
      e.preventDefault();
      const newNotes = e.currentTarget.value;
      setNotes(newNotes);
      if (scope.id) {
        debounce((newNotes) => {
          updateScope(scope.id, { notes: newNotes }).catch((e) => {
            console.error(`could not update newNotes on scope id ${scope.id}`, e);
          });
        }, 100)(newNotes);
      }
    },
    [updateScope, scope, setNotes]
  );

  /**
   * Flush the latest notes value when the field loses focus.
   *
   * @param {void}
   * @returns {void}
   */
  const handleNotesBlur = useCallback(() => {
    if (scope.id) {
      updateScope(scope.id, { notes }).catch((e) => {
        console.error(`could not update notes on scope id ${scope.id}`, e);
      });
    }
  }, [notes, scope.id, updateScope]);

  // Extra DATA sections section
  /**
   * Prepend a blank extra-notes section and persist the new list.
   *
   * @param {void}
   * @returns {void}
   */
  const handleAddExtraDataSection = useCallback(() => {
    const nextSections = [
      { title: "Extra Notes", body: "" },
      ...extraDataSectionsRef.current,
    ];
    extraDataSectionsRef.current = nextSections;
    setExtraDataSections(nextSections);
    if (scope.id) {
      updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
        console.error(
          `could not update extra_data_sections on scope id ${scope.id}`,
          err
        );
      });
    }
  }, [scope.id, updateScope]);

  /**
   * Remove one extra-data section by index and persist the remaining list.
   *
   * @param {number} index Section index to remove.
   * @returns {void}
   */
  const performDeleteExtraDataSection = useCallback(
    (index) => {
      const nextSections = extraDataSectionsRef.current.filter((_, i) => i !== index);
      extraDataSectionsRef.current = nextSections;
      setExtraDataSections(nextSections);

      if (scope.id) {
        updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
          console.error(
            `could not update extra_data_sections on scope id ${scope.id}`,
            err
          );
        });
      }
    },
    [scope.id, updateScope]
  );

  /**
   * Open the confirm dialog for deleting one extra-data section.
   *
   * @param {number} index Section index pending delete.
   * @returns {void}
   */
  const handleRequestDeleteExtraDataSection = useCallback((index) => {
    setPendingExtraDataSectionDeleteIndex(index);
  }, []);

  /**
   * Confirm and persist deletion of the pending extra-data section.
   *
   * @param {void}
   * @returns {void}
   */
  const handleConfirmDeleteExtraDataSection = useCallback(() => {
    if (pendingExtraDataSectionDeleteIndex == null) return;
    const index = pendingExtraDataSectionDeleteIndex;
    setPendingExtraDataSectionDeleteIndex(null);
    performDeleteExtraDataSection(index);
  }, [pendingExtraDataSectionDeleteIndex, performDeleteExtraDataSection]);

  /**
   * Debounce-save an extra-data section title.
   *
   * @param {number} index Section index.
   * @param {string} newTitle Next title text.
   * @returns {void}
   */
  const handleExtraDataSectionTitleChange = useCallback((index, newTitle) => {
    const nextSections = extraDataSectionsRef.current.map((s, i) =>
      i === index ? { ...s, title: newTitle } : s
    );
    extraDataSectionsRef.current = nextSections;
    setExtraDataSections(nextSections);
    if (scope.id) {
      debounce((nextSections) => {
        updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
          console.error(
            `could not update extra_data_sections on scope id ${scope.id}`,
            err
          );
        });
      }, 100)(nextSections);
    }
  }, [scope.id, updateScope]);

  /**
   * Flush extra-data sections when a title/body field loses focus.
   *
   * @param {void}
   * @returns {void}
   */
  const handleExtraDataSectionsBlur = useCallback(() => {
    if (scope.id) {
      updateScope(scope.id, {
        extra_data_sections: extraDataSectionsRef.current,
      }).catch((err) => {
        console.error(
          `could not update extra_data_sections on scope id ${scope.id}`,
          err
        );
      });
    }
  }, [scope.id, updateScope]);

  /**
   * Build a change handler that debounce-saves one extra-data section body.
   *
   * @param {number} index Section index.
   * @returns {(e: import('react').ChangeEvent<HTMLTextAreaElement>) => void}
   */
  const handleExtraDataSectionBodyChange = useCallback(
    (index) => (e) => {
      e.preventDefault();
      const newBody = e.currentTarget.value;
      const nextSections = extraDataSectionsRef.current.map((s, i) =>
        i === index ? { ...s, body: newBody } : s
      );
      extraDataSectionsRef.current = nextSections;
      setExtraDataSections(nextSections);
      if (scope.id) {
        debounce((nextSections) => {
          updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
            console.error(
              `could not update extra_data_sections on scope id ${scope.id}`,
              err
            );
          });
        }, 100)(nextSections);
      }
    },
    [scope, updateScope]
  );

  /**
   * Background class for pending delete or copy-source highlight.
   *
   * @param {void}
   * @returns {string}
   */
  const getScopeClasses = () => {
    if (scopesToDelete.includes(scope.id)) {
      return INTERACTION_COLORS.danger.bg;
    }
    if (
      copyScopesDateMatch ||
      (copyScopeOrigin && copyScopeOrigin[0] === scope.id)
    ) {
      return INTERACTION_COLORS.success.bg;
    }
    return "bg-gray-50";
  };

  /**
   * Pulse animation class while this scope is in the pending-delete set.
   *
   * @param {void}
   * @returns {string}
   */
  const getAnimationClass = () => {
    return scopesToDelete.includes(scope.id) ? ANIMATION_CONFIG.pulse : "";
  };

  // Locked cards keep edit controls blocked, but notes can opt back into hover/scroll/expand.
  const lockedMutedClass = isDayLocked
    ? DISPATCH_DAY_LOCKED_UI.scopeCardMutedClass
    : "";
  const lockedNotesClass = isDayLocked
    ? `relative z-[10] pointer-events-auto ${DISPATCH_DAY_LOCKED_UI.scopeCardMutedClass} hover:opacity-100 transition-opacity duration-200`
    : "";

  return (
    <>
      {nullScope ? (
        <></>
      ) : (
        <div
          className={`relative pt-[2px] ${getScopeClasses()} flex flex-col gap-y-1 mb-2 ${BORDERS.radius.sm} outline outline-1 outline-gray-300 ${getAnimationClass()}`}
        >
          {scope.skipped ? (
            <SkippedDayOverlay
              className="top-[32px]"
              aspectId={scope.id}
              reason={scope.skipped_reason ?? ""}
              module="dispatch"
              onReasonChange={(nextReason) =>
                applyScopePatch(scope.id, { skipped_reason: nextReason })
              }
            />
          ) : null}
          <div className={`flex flex-col ${lockedMutedClass}`}>
            <div
              className={
                "mx-2 mb-0 grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] gap-0 text-gray-800"
              }
            >
              {scope.scope ? (
                <div
                  className={
                    "flex flex-row font-semibold items-center min-h-[24px] space-x-1 col-span-1 row-span-1"
                  }
                >
                  <p className="pr-2 leading-tight min-h-[24px] pt-[2px]">
                    {scope.scope.title}
                  </p>
                </div>
              ) : (
                <div className="flex justify-center items-center col-span-1 row-span-1">
                  <button
                    className={
                      "font-medium rounded w-full max-w-xs outline outline-gray-300 outline-1 bg-blue-50 hover:bg-blue-100"
                    }
                    onClick={() => setIsEditScopeModalOpen(true)}
                  >
                    Select Scope
                  </button>
                </div>
              )}
              <div className="col-span-1 row-span-1 flex items-center justify-end gap-2 text-gray-600">
                {!isReadOnly && scope.scope ? (
                  <button
                    className="text-[0.9rem]"
                    onClick={() => setIsEditScopeModalOpen(true)}
                  >
                    <GoPencil />
                  </button>
                ) : null}
                {!isReadOnly ? (
                  <button
                    className="text-base"
                    onClick={handleOpenScopeActionMenu}
                  >
                    <IoEllipsisHorizontal />
                  </button>
                ) : null}
              </div>
              {scope.quantity ? (
                <div className="text-gray-600 text-sm col-span-1 row-span-1 max-h-6">
                  {scope.quantity} {scope.scope?.unit?.name || "Qty"}
                </div>
              ) : null}
            </div>

            {/*Custom values section*/}
            {scope["custom_address"] && (
              <AddressMapsLink
                address={scope["custom_address"]}
                className="block text-gray-600 text-sm mx-2"
              />
            )}
            {scope["custom_time"] && (
              <div className={"text-gray-600 text-sm mx-2"}>
                {timeToString(scope["custom_time"])}
              </div>
            )}
          </div>

          {/*Workers section*/}
          <div className={lockedMutedClass}>
            <ScopeWorkersSection scope={scope} />
          </div>

          {/*Details section*/}
          <div className={lockedMutedClass}>
            <ScopeDetailsSection scope={scope} />
          </div>

          {/* Main notes + extra data sections */}
          {captureComponent ? (
            <>
              <div className={lockedNotesClass}>
                <LinkifiedTextArea
                  staticDisplay
                  label="Notes"
                  value={notes}
                  placeholder="Add notes"
                  className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                />
              </div>
              {extraDataSections.map((sec, index) => (
                <div key={index} className={lockedNotesClass}>
                  <LinkifiedTextArea
                    staticDisplay
                    label={sec.title || "Section"}
                    value={sec.body}
                    placeholder="Add content"
                    className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className={lockedNotesClass}>
                <LinkifiedTextArea
                  label="Notes"
                  placeholder="Add notes"
                  value={notes}
                  onChange={handleEditNotes}
                  onBlur={handleNotesBlur}
                  readOnly={isReadOnly || isDayLocked}
                  onAdd={handleAddExtraDataSection}
                  className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                />
              </div>
              {extraDataSections.map((sec, index) => (
                <div key={index} className={lockedNotesClass}>
                  <LinkifiedTextArea
                    label={sec.title || "Section"}
                    readOnly={isReadOnly || isDayLocked}
                    placeholder="Add content"
                    value={sec.body}
                    onChange={handleExtraDataSectionBodyChange(index)}
                    onLabelChange={(newTitle) =>
                      handleExtraDataSectionTitleChange(index, newTitle)
                    }
                    onBlur={handleExtraDataSectionsBlur}
                    onLabelBlur={handleExtraDataSectionsBlur}
                    onDelete={() => handleRequestDeleteExtraDataSection(index)}
                    className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                  />
                </div>
              ))}
            </>
          )}
          {isDayLocked ? (
            <div
              className="absolute inset-0 z-[5] "
              title={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
              aria-label={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
            />
          ) : null}
        </div>
      )}
      <WorkScopeModal
        mode="edit"
        isOpen={isEditScopeModalOpen}
        onClose={() => setIsEditScopeModalOpen(false)}
        onConfirm={handleEditWorkScopeAspect}
        job={scope.job}
        date={date}
        initialScope={scope.scope}
        initialQuantity={scope.quantity}
      />
      <ConfirmModal
        isOpen={pendingExtraDataSectionDeleteIndex != null}
        title={"Remove extra data section?"}
        list={
          pendingExtraDataSectionDeleteIndex != null
            ? [
              extraDataSections[pendingExtraDataSectionDeleteIndex]?.title?.trim() ||
              "Section",
            ]
            : []
        }
        text={
          "Are you sure? This action cannot be undone."
        }
        buttonText={"Remove"}
        onConfirm={handleConfirmDeleteExtraDataSection}
        onClose={() => setPendingExtraDataSectionDeleteIndex(null)}
      />
    </>
  );
}
